// Groups adjacent registry-declared activity on the same day. Invisible
// events stay out of the transcript and do not break the duration receipt.
import { type ReactNode } from 'react';

import {
    type ProjectedActivityBoundary,
    type ProjectedActivityInput,
    type ProjectedActivityOccurrence,
    type ProjectedOrdinaryOccurrence,
    type ProjectedPlaceholderOccurrence,
    type ProjectedTranscriptRow,
} from '@apps/web/features/phoebe_chat_v3/model/projection';
import { type ProjectedTranscriptRow as ProjectedViewRow } from '@apps/web/features/phoebe_chat_v3/registry/types';

export type ActivityGroupInput = Readonly<{
    readonly kind: 'activity_group';
    readonly groupId: string;
    readonly summary: string;
    readonly members: ReadonlyArray<ProjectedTranscriptRow>;
}>;

export type ActivityInput = ProjectedTranscriptRow | ActivityGroupInput;

export type ActiveWorkItem = Readonly<{
    readonly id: string;
    readonly reason: string;
}> &
    (
        | Readonly<{
              readonly kind: 'check_in';
              readonly scheduledAtMs: number;
              readonly fireAtMs: number;
          }>
        | Readonly<{ readonly kind: 'watch' }>
    );

export type ActiveWorkPartition = Readonly<{
    readonly transcriptOccurrences: ReadonlyArray<ProjectedActivityInput>;
    readonly activeWork: ReadonlyArray<ActiveWorkItem>;
}>;

/**
 * Reports pending check-ins as active work while leaving every occurrence
 * in the transcript. The harness draws no chrome for them; the signal keeps
 * the stop control offered and the conversation non-empty while a check-in
 * is the run's only remaining work. Interactive work becomes a top-level
 * transcript row. A pending check-in is a receipt (see
 * `ActivityGroupDeclaration.receipt`), so grouping turns it into its own
 * row rather than folding it away.
 */
export function partitionActiveWorkOccurrences(
    inputs: ReadonlyArray<ProjectedActivityInput>,
    interactionOwnerIds: ReadonlySet<string>,
): ActiveWorkPartition {
    const transcriptOccurrences: Array<ProjectedActivityInput> = [];
    const activeWork: Array<ActiveWorkItem> = [];
    for (const input of inputs) {
        if (
            input.kind !== 'activity_occurrence' ||
            input.group.phase !== 'active'
        ) {
            transcriptOccurrences.push(input);
            continue;
        }
        if (interactionOwnerIds.has(input.occurrenceId)) {
            transcriptOccurrences.push({
                kind: 'ordinary_occurrence',
                rowId: input.rowId,
                occurrenceId: input.occurrenceId,
                eventIds: input.eventIds,
                day: input.day,
                node: input.node,
            });
            continue;
        }
        if (input.group.activeWork.kind === 'check_in') {
            activeWork.push({
                id: input.occurrenceId,
                reason: input.group.presentSummary,
                ...input.group.activeWork,
            });
        }
        transcriptOccurrences.push(input);
    }
    return { transcriptOccurrences, activeWork };
}

export type OccurrenceViewRow = Readonly<{
    readonly kind: 'occurrence';
    readonly rowKey: string;
    readonly occurrence: ProjectedTranscriptRow;
}>;

export type ActivityGroupSummaryViewRow = Readonly<{
    readonly kind: 'activity_group_summary';
    readonly rowKey: string;
    readonly groupId: string;
    readonly summary: string;
    readonly memberCount: number;
    readonly expanded: boolean;
    readonly startedAtMs: number | null;
    readonly live: boolean;
}>;

export type ActivityGroupMemberViewRow = Readonly<{
    readonly kind: 'activity_group_member';
    readonly rowKey: string;
    readonly groupId: string;
    readonly occurrence: ProjectedTranscriptRow;
}>;

export type TranscriptViewRow =
    | OccurrenceViewRow
    | ActivityGroupSummaryViewRow
    | ActivityGroupMemberViewRow;

export function flattenActivityRows(
    inputs: ReadonlyArray<ActivityInput>,
    expandedGroupIds: ReadonlySet<string>,
): ReadonlyArray<TranscriptViewRow> {
    const rows: Array<TranscriptViewRow> = [];
    for (const input of inputs) {
        if (input.kind !== 'activity_group') {
            rows.push({
                kind: 'occurrence',
                rowKey: input.occurrenceId,
                occurrence: input,
            });
            continue;
        }

        rows.push({
            kind: 'activity_group_summary',
            rowKey: `activity-group:${input.groupId}`,
            groupId: input.groupId,
            summary: input.summary,
            memberCount: input.members.length,
            expanded: expandedGroupIds.has(input.groupId),
            startedAtMs: null,
            live: false,
        });
    }
    return rows;
}

export type ConsecutiveActivityGroup = Readonly<{
    readonly kind: 'consecutive_activity_group';
    readonly groupId: string;
    readonly day: string;
    readonly members: ReadonlyArray<ProjectedActivityOccurrence>;
}>;

export type GroupedActivityItem =
    | ConsecutiveActivityGroup
    | ProjectedActivityBoundary
    | ProjectedOrdinaryOccurrence
    | ProjectedPlaceholderOccurrence;

const REASONING_SUMMARIES = new Set([
    'Thinking through the request',
    'Thought through the request',
]);

const HIDDEN_PLACEHOLDER_COPY = new Set([
    '',
    'Prepared the results',
    'Preparing the results',
    'Thought through the request',
    'Thinking through the request',
    'Checked back on the request',
    'Working on the request',
    'Completed the request',
]);

function memberIsReasoning(member: ProjectedActivityOccurrence): boolean {
    return (
        REASONING_SUMMARIES.has(member.group.presentSummary) &&
        REASONING_SUMMARIES.has(member.group.pastSummary)
    );
}

function memberNarration(member: ProjectedActivityOccurrence): string | null {
    const text =
        member.group.phase === 'active'
            ? member.group.presentSummary
            : member.group.pastSummary;
    const trimmed = text.trim();
    if (
        trimmed === '' ||
        HIDDEN_PLACEHOLDER_COPY.has(trimmed) ||
        memberIsReasoning(member)
    ) {
        return null;
    }
    return trimmed;
}

/**
 * What the folded line says while the group is live: the newest active
 * step in the present tense ("Checking agency records…"), otherwise the
 * newest finished step in the past tense ("Checked agency records") with
 * the spinner still turning while Phoebe thinks about it, and Thinking
 * only before any step has copy to show.
 */
function liveStepCopy(
    members: ReadonlyArray<ProjectedActivityOccurrence>,
): string {
    // Once Phoebe has moved on to new reasoning, an earlier tool's past
    // narration no longer describes the current step.
    const newest = members[members.length - 1];
    if (newest !== undefined && memberIsReasoning(newest)) {
        return 'Thinking…';
    }
    let latestFinished: string | null = null;
    for (let index = members.length - 1; index >= 0; index -= 1) {
        const member = members[index];
        if (member === undefined) {
            continue;
        }
        const narration = memberNarration(member);
        if (narration === null) {
            continue;
        }
        if (member.group.phase === 'active') {
            return `${narration}…`;
        }
        latestFinished ??= narration;
    }
    return latestFinished ?? 'Thinking…';
}

function groupedActivity(
    members: ReadonlyArray<ProjectedActivityOccurrence>,
): ConsecutiveActivityGroup {
    const first = members[0];
    if (first === undefined) {
        throw new Error('An activity group must contain at least one member.');
    }
    return {
        kind: 'consecutive_activity_group',
        groupId: first.occurrenceId,
        day: first.day,
        members,
    };
}

const NO_SURFACED_OWNERS: ReadonlySet<string> = new Set();

function isHiddenPlaceholder(input: ProjectedPlaceholderOccurrence): boolean {
    return HIDDEN_PLACEHOLDER_COPY.has(input.customerCopy.trim());
}

function isEmptyRenderNode(node: ReactNode): boolean {
    if (node === null || node === undefined || node === false || node === '') {
        return true;
    }
    if (typeof node === 'string') {
        return node.trim() === '';
    }
    if (typeof node !== 'object') {
        return false;
    }
    if (Array.isArray(node)) {
        return node.every(isEmptyRenderNode);
    }
    if (!('type' in node) || !('props' in node)) {
        return false;
    }
    const element = node as {
        readonly type: string | symbol | ((...args: never) => ReactNode);
        readonly props?: { readonly children?: ReactNode };
    };
    // Custom components own their markup; only empty host nodes are skipped.
    if (typeof element.type === 'function') {
        return false;
    }
    const children = element.props?.children;
    if (children === undefined) {
        return true;
    }
    return isEmptyRenderNode(children);
}

function isTransparentInput(input: ProjectedActivityInput): boolean {
    switch (input.kind) {
        case 'boundary':
            return true;
        case 'placeholder':
            return isHiddenPlaceholder(input);
        case 'ordinary_occurrence':
            return isEmptyRenderNode(input.node);
        case 'activity_occurrence':
            return false;
        default: {
            const exhaustiveInput: never = input;
            return exhaustiveInput;
        }
    }
}

/**
 * Consecutive same-day activity folds into one Thought/Worked duration row,
 * including across invisible events. An owner that carries a card or a
 * table, or a receipt event, is surfaced instead: it leaves the group and
 * keeps its own row, because the summary line cannot stand in for something
 * the reader has to act on or read.
 */
export function groupActivityOccurrences(
    inputs: ReadonlyArray<ProjectedActivityInput>,
    surfacedOwnerIds: ReadonlySet<string> = NO_SURFACED_OWNERS,
): ReadonlyArray<GroupedActivityItem> {
    const grouped: Array<GroupedActivityItem> = [];
    let pendingMembers: Array<ProjectedActivityOccurrence> = [];

    function flushPendingMembers(): void {
        if (pendingMembers.length === 0) {
            return;
        }
        grouped.push(groupedActivity(pendingMembers));
        pendingMembers = [];
    }

    for (const input of inputs) {
        if (input.kind === 'boundary') {
            // A boundary renders nothing either way. Activity folds across
            // an intentionally invisible event; an unknown or malformed one
            // is a safety boundary the group cannot vouch for, so it ends.
            if (input.reason !== 'invisible') {
                flushPendingMembers();
            }
            continue;
        }
        if (isTransparentInput(input)) {
            continue;
        }
        if (input.kind !== 'activity_occurrence') {
            flushPendingMembers();
            grouped.push(input);
            continue;
        }
        if (
            surfacedOwnerIds.has(input.occurrenceId) ||
            input.group.receipt === true
        ) {
            flushPendingMembers();
            grouped.push({
                kind: 'ordinary_occurrence',
                rowId: input.rowId,
                occurrenceId: input.occurrenceId,
                eventIds: input.eventIds,
                day: input.day,
                node: input.node,
            });
            continue;
        }
        const previous = pendingMembers[pendingMembers.length - 1];
        if (previous !== undefined && previous.day !== input.day) {
            flushPendingMembers();
        }
        pendingMembers.push(input);
    }
    flushPendingMembers();
    return grouped;
}

function isVisibleItem(input: GroupedActivityItem): boolean {
    switch (input.kind) {
        case 'boundary':
            return false;
        case 'ordinary_occurrence':
            return !isEmptyRenderNode(input.node);
        case 'placeholder':
            return !isHiddenPlaceholder(input);
        case 'consecutive_activity_group':
            return true;
        default: {
            const exhaustiveInput: never = input;
            return exhaustiveInput;
        }
    }
}

const PENDING_STEP_GROUP_ID = 'pending-step';

export function isUserMessage(input: ProjectedActivityInput): boolean {
    return (
        input.kind === 'ordinary_occurrence' &&
        input.messageSender !== undefined &&
        input.messageSender.startsWith('user:')
    );
}

export function isAssistantAnswer(input: ProjectedActivityInput): boolean {
    return (
        input.kind === 'ordinary_occurrence' &&
        input.messageSender === 'assistant'
    );
}

/**
 * True when the transcript's newest visible item is Phoebe's written answer.
 * The response the user was waiting on has already appeared, so a generic
 * thinking indicator below it would only lag the terminal event; if the run
 * genuinely continues, its next step becomes a live trailing group, which
 * counts as newer than the answer here.
 */
export function latestVisibleInputIsAssistantAnswer(
    inputs: ReadonlyArray<GroupedActivityItem>,
    liveLastGroup: boolean,
): boolean {
    // Mirrors projectActivityRows: the trailing group is the last one with
    // no visible row after it, and it is visible only while live.
    let trailingGroup: GroupedActivityItem | null = null;
    for (const input of inputs) {
        if (input.kind === 'consecutive_activity_group') {
            trailingGroup = input;
        } else if (isVisibleItem(input)) {
            trailingGroup = null;
        }
    }
    for (let index = inputs.length - 1; index >= 0; index -= 1) {
        const input = inputs[index];
        if (input === undefined || !isVisibleItem(input)) {
            continue;
        }
        if (input.kind === 'consecutive_activity_group') {
            const live =
                input === trailingGroup &&
                (liveLastGroup ||
                    input.members.some(
                        (member) => member.group.phase === 'active',
                    ));
            if (!live) {
                continue;
            }
            return false;
        }
        return isAssistantAnswer(input);
    }
    return false;
}

export type HeldPresentations = Readonly<{
    readonly inputs: ReadonlyArray<ProjectedActivityInput>;
    /** The answer each presentation owner sits behind, moved or not. */
    readonly answerByOwnerId: ReadonlyMap<string, string>;
}>;

// The nearest answer of the same turn in one direction: a user message ends
// the turn before another answer is found.
function answerIndexFrom(
    inputs: ReadonlyArray<ProjectedActivityInput>,
    start: number,
    step: 1 | -1,
): number {
    for (
        let index = start;
        index >= 0 && index < inputs.length;
        index += step
    ) {
        const candidate = inputs[index];
        if (candidate === undefined || isUserMessage(candidate)) {
            return -1;
        }
        if (isAssistantAnswer(candidate)) {
            return index;
        }
    }
    return -1;
}

/**
 * A table or card Phoebe prepared reads best after the sentence that
 * introduces it, but its event lands first. Each presentation owner moves
 * behind the next written answer of the same turn. While the run is still
 * working and no answer has landed, the owner waits as an invisible
 * boundary; a turn that ends without an answer shows it where it was. The
 * returned map is the one record of which answer an owner belongs to,
 * including an owner that already sat behind its answer.
 */
export function holdPresentationsUntilAnswer(
    inputs: ReadonlyArray<ProjectedActivityInput>,
    presentationOwnerIds: ReadonlySet<string>,
    live: boolean,
): HeldPresentations {
    const movedAfter = new Map<number, Array<ProjectedActivityInput>>();
    const moved = new Set<number>();
    const waiting = new Set<number>();
    const answerByOwnerId = new Map<string, string>();
    for (const [index, input] of inputs.entries()) {
        if (
            input.kind !== 'activity_occurrence' ||
            !presentationOwnerIds.has(input.occurrenceId)
        ) {
            continue;
        }
        const answerIndex = answerIndexFrom(inputs, index + 1, 1);
        if (answerIndex !== -1) {
            moved.add(index);
            const owners = movedAfter.get(answerIndex);
            if (owners === undefined) {
                movedAfter.set(answerIndex, [input]);
            } else {
                owners.push(input);
            }
        } else if (live) {
            waiting.add(index);
        }
        const ownedAnswerIndex =
            answerIndex !== -1
                ? answerIndex
                : answerIndexFrom(inputs, index - 1, -1);
        const answer = inputs[ownedAnswerIndex];
        if (answer !== undefined) {
            answerByOwnerId.set(input.occurrenceId, answer.occurrenceId);
        }
    }
    if (moved.size === 0 && waiting.size === 0) {
        return { inputs, answerByOwnerId };
    }
    const held = inputs.flatMap((input, index) => {
        if (moved.has(index)) {
            return [];
        }
        if (waiting.has(index)) {
            return [
                {
                    kind: 'boundary' as const,
                    reason: 'invisible' as const,
                    rowId: `boundary:${input.occurrenceId}`,
                    occurrenceId: input.occurrenceId,
                    eventIds: input.eventIds,
                    day: input.day,
                },
            ];
        }
        return [input, ...(movedAfter.get(index) ?? [])];
    });
    return { inputs: held, answerByOwnerId };
}

/**
 * Activity shows only while it runs: the group that ends the transcript is
 * one line naming its current step, and every other group leaves the
 * transcript, including one whose tool never reported back before a later
 * message. `liveLastGroup` marks that trailing group as still running even
 * when none of its members is active. `pendingStepCopy` is the petal row's
 * text when the run has nothing of its own to show — "Thinking…" while it
 * works, a waiting message while it needs the user: when no live group ends
 * the transcript, that row stands in. Between them, one conversation shows
 * at most one progress row.
 */
export function projectActivityRows(
    inputs: ReadonlyArray<GroupedActivityItem>,
    liveLastGroup = false,
    pendingStepCopy: string | null = null,
): ReadonlyArray<ProjectedViewRow> {
    // The trailing group is the last one with no visible row after it.
    let trailingGroup: ConsecutiveActivityGroup | null = null;
    for (const input of inputs) {
        if (input.kind === 'consecutive_activity_group') {
            trailingGroup = input;
        } else if (isVisibleItem(input)) {
            trailingGroup = null;
        }
    }
    function isLive(group: ConsecutiveActivityGroup): boolean {
        return (
            group === trailingGroup &&
            (liveLastGroup ||
                group.members.some((member) => member.group.phase === 'active'))
        );
    }
    const visibleItems = inputs.filter(
        (input) =>
            isVisibleItem(input) &&
            (input.kind !== 'consecutive_activity_group' || isLive(input)),
    );
    const showDayHeadings =
        new Set(visibleItems.map((item) => item.day)).size > 1;
    const rows: Array<ProjectedViewRow> = [];
    let previousRenderedDay: string | null = null;
    function appendDayHeading(
        input: Exclude<GroupedActivityItem, ProjectedActivityBoundary>,
    ): void {
        if (!showDayHeadings || input.day === previousRenderedDay) {
            return;
        }
        const anchorId =
            input.kind === 'consecutive_activity_group'
                ? `activity-group:${input.groupId}`
                : input.rowId;
        rows.push({
            kind: 'day_heading',
            rowId: `day-heading:${anchorId}`,
            day: input.day,
        });
        previousRenderedDay = input.day;
    }
    let trailingGroupLive = false;
    for (const input of inputs) {
        switch (input.kind) {
            case 'boundary':
                break;
            case 'ordinary_occurrence':
                if (isEmptyRenderNode(input.node)) {
                    break;
                }
                appendDayHeading(input);
                rows.push({
                    kind: 'occurrence',
                    rowId: input.rowId,
                    occurrenceId: input.occurrenceId,
                    eventIds: input.eventIds,
                    node: input.node,
                    ...(input.messageSender === undefined
                        ? {}
                        : { messageSender: input.messageSender }),
                    ...(input.messageText === undefined
                        ? {}
                        : { messageText: input.messageText }),
                });
                break;
            case 'placeholder':
                if (isHiddenPlaceholder(input)) {
                    break;
                }
                appendDayHeading(input);
                rows.push({
                    kind: 'placeholder',
                    rowId: input.rowId,
                    occurrenceId: input.occurrenceId,
                    customerCopy: input.customerCopy,
                });
                break;
            case 'consecutive_activity_group': {
                if (!isLive(input)) {
                    break;
                }
                if (input === trailingGroup) {
                    trailingGroupLive = true;
                }
                appendDayHeading(input);
                rows.push({
                    kind: 'activity_group_summary',
                    rowId: `activity-group:${input.groupId}`,
                    groupId: input.groupId,
                    eventIds: input.members.flatMap(
                        (member) => member.eventIds,
                    ),
                    expanded: false,
                    summary: liveStepCopy(input.members),
                    memberCount: 0,
                    startedAtMs: null,
                    live: true,
                });
                break;
            }
            default: {
                const exhaustiveInput: never = input;
                return exhaustiveInput;
            }
        }
    }
    if (pendingStepCopy !== null && !trailingGroupLive) {
        rows.push({
            kind: 'activity_group_summary',
            rowId: `activity-group:${PENDING_STEP_GROUP_ID}`,
            groupId: PENDING_STEP_GROUP_ID,
            eventIds: [],
            expanded: false,
            summary: pendingStepCopy,
            memberCount: 0,
            startedAtMs: null,
            live: true,
        });
    }
    return rows;
}
